import { createModel, RematchDispatch } from '@rematch/core';
import { message } from 'antd';
import produce from 'immer';
import { AxiosError } from 'axios';

import { DEFAULT_PAGINATION } from '../../../commons/constants/constants';
import { applyTheme } from '../../../services/theme.service';
import { PaginationType } from '../../../types/CommonTypes';
import { ThemeData } from '../../../types/ThemeData';
import { storeName as themeEntity } from '../../entities/theme.entity';
import { RootModel } from '../../models';
import { HTTP_STATUS } from '../../../commons/constants/enum';
import { showMessage } from '../../../utils/messageError';

export const storeName = 'theme';

const initialState = {
  visible: false,
  browseVisible: false,
  tourTheme: null,
  ids: [],
  pagination: DEFAULT_PAGINATION,
  editId: null,
  loading: false,
  criteria: undefined,
};

export const theme: any = createModel<RootModel>()({
  name: storeName,
  state: { ...initialState },
  reducers: {
    setTourTheme: produce((state, payload) => {
      state.tourTheme = payload;
    }),
    setVisible: produce((state, payload) => {
      state.visible = payload;
    }),
    setBrowseVisible: produce((state, payload) => {
      state.browseVisible = payload;
    }),
    setEditId: produce((state, payload) => {
      state.editId = payload;
    }),
    setIds: produce((state, payload) => {
      state.ids = payload;
    }),
    setPagination: produce((state, payload) => {
      state.pagination = payload;
    }),
    setCriteria: produce((state, payload) => {
      state.criteria = payload;
    }),
    setLoading: produce((state, payload) => {
      state.loading = payload;
    }),
    resetState: produce((state, payload) => {
      return {
        ...initialState,
      };
    }),
  },
  effects: (dispatch: RematchDispatch<typeof theme>) => ({
    async reloadList(_, state) {
      const { criteria, pagination } = state[storeName];
      await this.filterThemes({ ...criteria, ...pagination });
    },
    async filterThemes(payload, state) {
      this.setLoading(true);
      try {
        const data: { content?: ThemeData[] } & PaginationType = await dispatch[themeEntity].load(
          payload
        );
        const { content, ...pagination } = data;
        this.setIds(content.map((item) => item.id));
        this.setPagination(pagination);
        this.setCriteria(payload);
      } catch (err) {
        message.error(`Fail to get theme list : ${err.message}`);
        console.error(err.message);
      } finally {
        this.setLoading(false);
      }
    },
    async pagingAsync(payload: { page?: number; size?: number }, rootState) {
      const { criteria = {} } = rootState[storeName];
      const request = {
        ...criteria,
        ...payload,
      };
      await this.filterThemes(request);
    },
    async applyTheme(payload, state) {
      this.setLoading(true);
      const { poiIcons } = payload;
      const { tourTheme } = state[storeName];
      const { id: tourId, lastModifiedDate } = state.tourDetail.data;

      try {
        const res = await applyTheme(tourId, { ...payload, lastModifiedDate });

        // If update is successfully, update poiIcons global value
        if (res && res.status === HTTP_STATUS.OK) {
          this.setTourTheme({ ...tourTheme, poiIcons });
        }

        message.success(`Theme applied`);
        return true;
      } catch (err) {
        const axiosError = err as AxiosError;
        console.error(axiosError.message);
        showMessage(axiosError, `Fail to apply theme: ${err.message}`);
        return false;
      } finally {
        this.setLoading(false);
      }
    },
    async deleteTheme(payload) {
      this.setLoading(true);
      try {
        await dispatch[themeEntity].delete(payload);
        message.success(`Deleted theme ${payload.name}`);
        this.reloadList();
      } catch (err) {
        message.error(`Fail to delete theme: ${err.message}`);
        console.error(err.message);
      } finally {
        this.setLoading(false);
      }
    },
    async saveTheme(payload, state) {
      const { tourTheme } = state[storeName];
      this.setLoading(true);
      try {
        let template;
        const params = { ...payload };
        if (params.id) {
          template = await dispatch[themeEntity].update(params);
          message.success('Update theme successfully');

          if (
            params.overrides &&
            params.overrides.length &&
            tourTheme &&
            params.overrides.includes(tourTheme.id)
          ) {
            this.setTourTheme({
              ...tourTheme,
              poiIcons: template.poiIcons,
            });
          }
          this.reloadList();
        } else {
          template = await dispatch[themeEntity].create(params);
          message.success('Add new theme successfully');
          this.filterThemes();
        }
        this.setVisible(false);
        this.setEditId(null);
        return template;
      } catch (err) {
        message.error(`Fail to save theme : ${err.message}`);
        console.error(err.message);
        return null;
      } finally {
        this.setLoading(false);
      }
    },
  }),
});

export default theme;
